import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, ChevronsUpDown, Loader2, Plus, Printer, Trash2, Truck, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { useQuotes } from '@/features/operations-projects';
import type { Quote } from '@/features/operations-projects';
import {
  useCalculateRates,
  useConfirmShipment,
  useCancelShipment,
  useLabelContext,
  useQuoteShipments,
  usePackagePresets,
  usePickupInstructions,
  useEligiblePickups,
} from '../hooks/useShippingLabels';
import { getShipmentLabelUrl } from '../api';
import { CreateRmaFromQuoteModal } from '@/features/rma';
import { useSites } from '@/features/sites/hooks/useSites';
import type { Site } from '@/features/sites/types';
import type {
  ConfirmedShipment,
  ConfirmShipmentInput,
  DestinationAddressInput,
  DestinationContactInput,
  EligiblePickup,
  LabelContext,
  PackagePreset,
  RateOption,
  RatesRequest,
} from '../types';

interface CreateShippingLabelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId?: string;
  mongoQuoteId?: string;
  prefill?: LabelContext;
}

type Step = 'existing' | 'details' | 'rates' | 'done';

// Origin address/contact and the ATEK Planete Courrier account number are now
// resolved server-side (see apps/rest_server/lib/planete-courrier-draft.js) —
// the frontend only sends the raw destination + phone.

const CUSTOM_SIZE_VALUE = '__custom__';

let packageKeySeq = 0;
function nextPackageKey(): string {
  packageKeySeq += 1;
  return `pkg-${packageKeySeq}`;
}

interface DestinationAddressForm {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
}

interface RecipientContactForm {
  name: string;
  email: string;
  phone: string;
}

interface PackageRowState {
  key: string;
  presetId: string | null;
  length: string;
  width: string;
  height: string;
  weight: string;
}

function destinationFromPrefill(prefill: LabelContext | undefined): DestinationAddressForm {
  const a = prefill?.destinationAddress;
  return {
    name: a?.name ?? '',
    addressLine1: a?.addressLine1 ?? '',
    addressLine2: a?.addressLine2 ?? '',
    city: a?.city ?? '',
    stateProvince: a?.stateProvince ?? '',
    postalCode: a?.postalCode ?? '',
    country: a?.country ?? 'CA',
  };
}

function recipientFromPrefill(prefill: LabelContext | undefined): RecipientContactForm {
  const c = prefill?.recipientContact;
  return { name: c?.name ?? '', email: c?.email ?? '', phone: c?.phone ?? '' };
}

function firstPackageFromPrefill(prefill: LabelContext | undefined): PackageRowState {
  return {
    key: nextPackageKey(),
    presetId: null,
    length: '',
    width: '',
    height: '',
    weight: String(prefill?.packages?.[0]?.weight ?? 0),
  };
}

function toDestinationAddressInput(form: DestinationAddressForm): DestinationAddressInput {
  return {
    name: form.name,
    streetAddress: form.addressLine1,
    suite: form.addressLine2 || null,
    city: form.city,
    provinceState: form.stateProvince,
    postalCode: form.postalCode,
    countryCode: form.country || 'CA',
  };
}

function toDestinationContactInput(form: RecipientContactForm): DestinationContactInput {
  return {
    name: form.name,
    email: form.email || null,
    phone: form.phone,
  };
}

function rateKey(rate: RateOption): string {
  return `${rate.carrierCode}::${rate.serviceAgreementId}`;
}

// ── Package row (preset picker OR custom dims + editable weight) ──

interface PackageRowProps {
  pkg: PackageRowState;
  index: number;
  presets: PackagePreset[];
  onChange: (next: PackageRowState) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function PackageRow({ pkg, index, presets, onChange, onRemove, canRemove }: PackageRowProps) {
  const { t } = useTranslation('planete-courrier');

  const handlePresetChange = (value: string) => {
    if (value === CUSTOM_SIZE_VALUE) {
      onChange({ ...pkg, presetId: null });
      return;
    }
    const preset = presets.find((p) => p.id === value);
    if (!preset) return;
    onChange({
      ...pkg,
      presetId: preset.id,
      length: preset.length != null ? String(preset.length) : pkg.length,
      width: preset.width != null ? String(preset.width) : pkg.width,
      height: preset.height != null ? String(preset.height) : pkg.height,
    });
  };

  const field = (key: 'length' | 'width' | 'height') => (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...pkg, presetId: null, [key]: e.target.value });
  };

  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {t('labelFlow.packages')} #{index + 1}
        </span>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('labelFlow.removePackage')}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${pkg.key}-preset`}>{t('labelFlow.packagePreset')}</Label>
        <Select value={pkg.presetId ?? CUSTOM_SIZE_VALUE} onValueChange={handlePresetChange}>
          <SelectTrigger id={`${pkg.key}-preset`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CUSTOM_SIZE_VALUE}>{t('labelFlow.customSize')}</SelectItem>
            {presets.map((preset) => (
              <SelectItem key={preset.id} value={preset.id}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label>{t('labelFlow.dimensions')} ({t('presets.unitCm')})</Label>
        <div className="grid grid-cols-3 gap-2">
          <Input
            aria-label={t('presets.length')}
            type="number"
            inputMode="decimal"
            placeholder={t('presets.length')}
            value={pkg.length}
            onChange={field('length')}
          />
          <Input
            aria-label={t('presets.width')}
            type="number"
            inputMode="decimal"
            placeholder={t('presets.width')}
            value={pkg.width}
            onChange={field('width')}
          />
          <Input
            aria-label={t('presets.height')}
            type="number"
            inputMode="decimal"
            placeholder={t('presets.height')}
            value={pkg.height}
            onChange={field('height')}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${pkg.key}-weight`}>
          {t('labelFlow.weight')} ({t('labelFlow.weightUnit')})
        </Label>
        <Input
          id={`${pkg.key}-weight`}
          type="number"
          inputMode="decimal"
          value={pkg.weight}
          onChange={(e) => onChange({ ...pkg, weight: e.target.value })}
        />
      </div>
    </div>
  );
}

// Format a Galaxie delivery ETA (ISO with offset) as a short Montreal-time label,
// e.g. "Wed, Aug 12, 5:00 p.m." — this is what distinguishes the service tiers
// (they share the carrier name; only the delivery time and price differ).
function formatEta(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal',
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(d);
}

// ATEK ships from one configured origin — a SUITE inside a commercial building
// (5490 boul. Thimens, suite 240) — so the driver collects at the office, not at a
// street-level front door. Pre-select it when the carrier offers it; the operator can
// still change it before buying. The suite number itself rides on the origin address,
// and any extra detail goes in the free-text pickup note.
const DEFAULT_PICKUP_LOCATION_CODE = 'Office';

// Galaxie caps the driver note at 50 characters.
const MAX_PICKUP_NOTES = 50;

// Radix Select forbids empty-string item values, so the "no attachment" choice
// uses a sentinel that maps back to '' (no attach) in state.
const ATTACH_NONE = '__none__';

// "PUR-777 (Oct. 17, 13:00 – 15:00)" — Montreal wall-clock, since Galaxie's
// fromTime/toTime are offset-carrying ISO strings.
function formatEligiblePickupOption(p: EligiblePickup): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal',
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
  return `${p.number} (${fmt.format(new Date(p.fromTime))} – ${fmt.format(new Date(p.toTime))})`;
}

// ── Pickup window helpers (America/Montreal, DST-aware) ──
// Galaxie rejects a pickup whose From-Time is in the past ("must be later or
// equals to now") or more than 7 days out. These keep the operator's window valid
// on the client so the billed confirm call doesn't fail with a raw vendor 400.

// Today's date as YYYY-MM-DD in America/Montreal.
function montrealToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

// A Montreal date `days` from today, as YYYY-MM-DD (used for the +7-day cap).
function montrealDatePlus(days: number): string {
  const [y, mo, d] = montrealToday().split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d + days)).toISOString().slice(0, 10);
}

// The absolute instant for a Montreal wall-clock date+time, mirroring the backend's
// montrealIso (apps/rest_server/routes/v4/planete-courrier.js) so the client's
// "is this in the past?" check matches exactly what Galaxie will receive.
function montrealInstant(dateStr: string, timeStr: string): Date {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  const guess = new Date(Date.UTC(y, (mo || 1) - 1, d || 1, h || 0, mi || 0));
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Montreal',
    timeZoneName: 'shortOffset',
  }).formatToParts(guess).find((p) => p.type === 'timeZoneName')?.value || 'GMT-4';
  const m = label.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
  const sign = m ? m[1] : '-';
  const oh = m ? String(m[2]).padStart(2, '0') : '04';
  const om = m && m[3] ? m[3] : '00';
  return new Date(`${dateStr}T${timeStr}:00${sign}${oh}:${om}`);
}

// HH:mm -> minutes since midnight.
function timeToMinutes(hhmm: string): number {
  const [h, m] = (hhmm || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// minutes since midnight (clamped within the day) -> HH:mm.
function minutesToTime(total: number): string {
  const clamped = Math.max(0, Math.min(total, 23 * 60 + 59));
  const hh = String(Math.floor(clamped / 60)).padStart(2, '0');
  const mm = String(clamped % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Current Montreal wall-clock time + `bufferMin`, rounded up to the next :00/:30.
// Used as the "From" default when a pickup is scheduled for today so it isn't
// stuck at a past 09:00.
function nextRoundedMontrealTime(bufferMin = 30): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '9');
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return minutesToTime(Math.ceil((h * 60 + m + bufferMin) / 30) * 30);
}

// ── Rate list (step 2) ──

interface RateListProps {
  rates: RateOption[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}

function RateList({ rates, selectedKey, onSelect }: RateListProps) {
  const { t } = useTranslation('planete-courrier');

  if (rates.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-6">{t('labelFlow.noRates')}</p>;
  }

  return (
    <ul className="space-y-2" role="radiogroup" aria-label={t('labelFlow.chooseCarrier')}>
      {rates.map((rate) => {
        const key = rateKey(rate);
        const selected = key === selectedKey;
        return (
          <li key={key}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!rate.isAvailable}
              onClick={() => onSelect(key)}
              className={cn(
                'w-full text-left rounded-md border p-3 flex items-center justify-between gap-3',
                !rate.isAvailable && 'opacity-50 cursor-not-allowed',
                selected ? 'border-primary bg-primary/5' : 'border-input',
              )}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">
                  {rate.serviceName || rate.carrierCode}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {formatEta(rate.deliveryEta)
                    ? `${t('labelFlow.deliveryBy', 'Delivery by')} ${formatEta(rate.deliveryEta)}`
                    : rate.carrierCode}
                </div>
              </div>
              <div className="font-medium shrink-0">{formatCurrency(rate.priceTotal)}</div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ── Main modal ──

export function CreateShippingLabelModal({
  open,
  onOpenChange,
  quoteId,
  mongoQuoteId,
  prefill,
}: CreateShippingLabelModalProps) {
  const { t } = useTranslation('planete-courrier');

  const calculateRates = useCalculateRates();
  const confirmShipment = useConfirmShipment();
  const cancelShipment = useCancelShipment();
  const labelContext = useLabelContext();
  const { data: presets } = usePackagePresets();

  const [step, setStep] = useState<Step>('details');

  // Quote picker (only shown when launched without a quoteId/prefill).
  const hasExternalPrefill = Boolean(quoteId || prefill);
  const [pickedQuote, setPickedQuote] = useState<Quote | null>(null);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [quotePopoverOpen, setQuotePopoverOpen] = useState(false);
  const { data: quotesData, isLoading: quotesLoading } = useQuotes(
    { search: quoteSearch || undefined, pageLength: 50, sortKey: 'createdAt', sortDirection: -1 },
    { enabled: open && !hasExternalPrefill },
  );
  const quotes = useMemo(() => quotesData?.quotes ?? [], [quotesData]);
  const showQuotePicker = !hasExternalPrefill && !pickedQuote;

  // Details form state (seeded from prefill; fields stay editable and never
  // crash on partial/null prefill data).
  const [destination, setDestination] = useState<DestinationAddressForm>(() => destinationFromPrefill(prefill));
  const [recipient, setRecipient] = useState<RecipientContactForm>(() => recipientFromPrefill(prefill));
  const [referencesText, setReferencesText] = useState(() => (prefill?.references ?? []).join(', '));
  // Declared value defaults to 0 (Hugo): operations does not insure/declare the
  // full quote value by default. Still editable — Omar raises it when a shipment
  // genuinely needs a declared value.
  const [declaredValueText, setDeclaredValueText] = useState('0');
  const [packages, setPackages] = useState<PackageRowState[]>(() => [firstPackageFromPrefill(prefill)]);
  const [weightIsEstimate, setWeightIsEstimate] = useState(() => prefill?.weightIsEstimate ?? false);

  // Destination-site picker: the client account's saved sites (from PG), default-
  // selected to the quote's shipping site. Selecting a site fills the address
  // inputs; they stay fully editable afterward. Both come from the label context.
  const [accountId, setAccountId] = useState<string | null>(() => prefill?.accountId ?? null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(() => prefill?.defaultSiteId ?? undefined);
  const { data: sitesData } = useSites(
    { accountId: accountId ?? undefined },
    { enabled: open && !!accountId },
  );
  const accountSites = useMemo(() => sitesData?.sites ?? [], [sitesData]);
  // Stable per-site value: prefer the mongo id (matches the quote's shipping-site
  // defaultSiteId so the default resolves); fall back to the PG id.
  const siteOptionValue = (s: Site) => s.mongoSiteId ?? s.id;
  const handleSiteSelect = (value: string) => {
    setSelectedSiteId(value);
    const site = accountSites.find((s) => siteOptionValue(s) === value);
    if (!site) return;
    setDestination({
      name: site.name ?? '',
      addressLine1: site.addressLine1 ?? '',
      addressLine2: site.addressLine2 ?? '',
      city: site.city ?? '',
      stateProvince: site.stateProvince ?? '',
      postalCode: site.postalCode ?? '',
      country: site.country ?? 'CA',
    });
  };

  // Optional carrier pickup window — Omar picks the date + time; a blank date means
  // no scheduled pickup (drop-off / carrier default).
  const [pickupDate, setPickupDate] = useState('');
  const [pickupFrom, setPickupFrom] = useState('09:00');
  const [pickupTo, setPickupTo] = useState('17:00');
  // The carrier's pickup location (Purolator's mandatory PickUpLocation). Empty
  // until a carrier is picked, since the legal values are per carrier.
  const [pickupLocation, setPickupLocation] = useState('');
  // ATTACH to an already-scheduled pickup request instead of creating a new one —
  // bundles this box into an existing driver stop. '' = no attachment. Exclusive
  // with the pickup window above (the portal's options are a radio, not a mix).
  const [attachPickupNumber, setAttachPickupNumber] = useState('');
  // Free-text note the driver reads on arrival ("Bureau 240, réception"). Travels on
  // the DRAFT, so it is captured here and sent with the rate request.
  const [pickupNotes, setPickupNotes] = useState('');

  // Rate-shop + buy state
  const [selectedRateKey, setSelectedRateKey] = useState<string | null>(null);
  const [created, setCreated] = useState<ConfirmedShipment | null>(null);
  const [cancelled, setCancelled] = useState(false);
  // Client-side pickup-window validation error, shown inline on the rates step
  // before the billed confirm call.
  const [buyError, setBuyError] = useState<string | null>(null);
  // "Also generate an RMA return label" (replacements): prepaid return in the box.
  const [alsoRma, setAlsoRma] = useState(false);
  const [rmaOpen, setRmaOpen] = useState(false);

  const effectiveQuoteId = quoteId ?? pickedQuote?.id;
  const effectiveMongoQuoteId = mongoQuoteId ?? pickedQuote?.mongo_quote_id;

  // Reset the RMA add-on whenever the modal (re)opens for a fresh shipment.
  useEffect(() => {
    if (open) {
      setAlsoRma(false);
      setRmaOpen(false);
    }
  }, [open]);

  // Labels already recorded against this quote (for the "already exists" warning
  // + the print/void list). Active = not voided.
  const { data: quoteShipments } = useQuoteShipments(effectiveQuoteId);
  const activeLabels = useMemo(
    () => (quoteShipments ?? []).filter((s) => s.status !== 'Cancelled'),
    [quoteShipments],
  );

  const applyLabelContext = (ctx: LabelContext) => {
    setDestination(destinationFromPrefill(ctx));
    setRecipient(recipientFromPrefill(ctx));
    setReferencesText((ctx.references ?? []).join(', '));
    setDeclaredValueText('0');
    setPackages([firstPackageFromPrefill(ctx)]);
    setWeightIsEstimate(ctx.weightIsEstimate ?? false);
    setAccountId(ctx.accountId ?? null);
    setSelectedSiteId(ctx.defaultSiteId ?? undefined);
  };

  const handleQuoteSelect = (quote: Quote) => {
    setPickedQuote(quote);
    setQuotePopoverOpen(false);
    labelContext.mutate(quote.id, { onSuccess: applyLabelContext });
  };

  // The modal is mounted persistently at its call sites (no `key`), so its
  // wizard state (step/created-shipment/rate-selection/picked-quote/form
  // fields) is otherwise mount-only and survives close+reopen. Whenever the
  // dialog transitions from closed -> open, reset everything to a fresh flow
  // seeded from the CURRENT props — otherwise reopening for a different quote
  // (or the same quote after a prior "done" screen) leaks the PRIOR
  // shipment's confirmation (stale shipment #/tracking #, "Print Again"
  // pointing at the wrong label). No-op on the very first render when `open`
  // already starts true — the useState initializers above already seed
  // correctly from `prefill` in that case.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    const reopened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!reopened) return;

    setStep('details');
    setCreated(null);
    setCancelled(false);
    setSelectedRateKey(null);
    setBuyError(null);
    setPickedQuote(null);
    setQuoteSearch('');
    calculateRates.reset();
    confirmShipment.reset();
    cancelShipment.reset();
    setDestination(destinationFromPrefill(prefill));
    setRecipient(recipientFromPrefill(prefill));
    setReferencesText((prefill?.references ?? []).join(', '));
    setDeclaredValueText('0');
    setPackages([firstPackageFromPrefill(prefill)]);
    setWeightIsEstimate(prefill?.weightIsEstimate ?? false);
    setAccountId(prefill?.accountId ?? null);
    setSelectedSiteId(prefill?.defaultSiteId ?? undefined);
    setPickupDate('');
    setPickupFrom('09:00');
    setPickupTo('17:00');
    setPickupLocation('');
    setPickupNotes('');
    setAttachPickupNumber('');
    // Intentionally depend on `open` only: this must fire exactly once per
    // close->open transition and read whatever `prefill`/`quoteId` are
    // current at that moment, not re-run every time a prop reference changes
    // while the dialog stays open (that would clobber in-progress edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Direct launch with only a quoteId (no prefill, no quote picker): self-fetch
  // the LabelContext instead of leaving the details form blank. Guarded so it
  // fires exactly once per (open, quoteId) pair; resets when the modal closes
  // so reopening (e.g. with a different quote) re-fetches.
  const selfFetchedQuoteIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      selfFetchedQuoteIdRef.current = null;
      return;
    }
    if (!quoteId || prefill) return;
    if (selfFetchedQuoteIdRef.current === quoteId) return;
    selfFetchedQuoteIdRef.current = quoteId;
    labelContext.mutate(quoteId, { onSuccess: applyLabelContext });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteId, prefill]);

  // When the modal opens for a quote that already has active (non-voided) labels,
  // land on the "existing labels" step first so the operator sees them (with
  // print/void) before creating another. Fires once per open, after the query
  // resolves; declared after the reset effect so its setStep wins the initial step.
  const existingCheckedRef = useRef(false);
  useEffect(() => {
    if (!open) { existingCheckedRef.current = false; return; }
    if (existingCheckedRef.current || quoteShipments === undefined) return;
    existingCheckedRef.current = true;
    if (activeLabels.length > 0) setStep('existing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteShipments]);

  const addPackage = () => {
    setPackages((prev) => [...prev, { key: nextPackageKey(), presetId: null, length: '', width: '', height: '', weight: '' }]);
  };

  const removePackage = (key: string) => {
    setPackages((prev) => (prev.length > 1 ? prev.filter((p) => p.key !== key) : prev));
  };

  const updatePackage = (next: PackageRowState) => {
    setPackages((prev) => prev.map((p) => (p.key === next.key ? next : p)));
  };

  const buildRatesRequest = (): RatesRequest => {
    const totalDeclaredValue = Number(declaredValueText) || 0;
    const perPackageDeclaredValue = packages.length > 0 ? totalDeclaredValue / packages.length : 0;
    const references = referencesText.split(',').map((v) => v.trim()).filter(Boolean);

    return {
      destinationAddress: toDestinationAddressInput(destination),
      destinationContact: toDestinationContactInput(recipient),
      packages: packages.map((p) => ({
        length: Number(p.length) || 0,
        width: Number(p.width) || 0,
        height: Number(p.height) || 0,
        weight: Number(p.weight) || 0,
        declaredValue: perPackageDeclaredValue,
      })),
      references,
      isImperialUnit: false,
      ...(pickupDate && pickupNotes.trim() ? { pickupInstructions: pickupNotes.trim() } : {}),
    };
  };

  const handleGetRates = () => {
    const req = buildRatesRequest();
    calculateRates.mutate(req, {
      onSuccess: () => {
        setSelectedRateKey(null);
        setStep('rates');
      },
    });
  };

  const ratesResult = calculateRates.data;
  // Only show purchasable services — unavailable tiers come back with price 0 / no carrier.
  const rateOptions = (ratesResult?.quotes ?? []).filter((r) => r.isAvailable);
  const selectedRate = rateOptions.find((r) => rateKey(r) === selectedRateKey) ?? null;

  // Pickup locations are per carrier, so they can only be fetched once a rate is
  // chosen — and only matter when a pickup is actually scheduled. A carrier with
  // an empty list (everyone but Purolator today) needs no location at all.
  const pickupLocationsQuery = usePickupInstructions(selectedRate?.carrierCode ?? null, Boolean(pickupDate));
  const pickupLocations = useMemo(() => pickupLocationsQuery.data ?? [], [pickupLocationsQuery.data]);
  // "No locations" and "not loaded yet" both read as an empty array, but they must not
  // behave the same: only a KNOWN list may reset the operator's choice.
  const pickupLocationsKnown = pickupLocationsQuery.data !== undefined;

  // Pre-select ATEK's usual spot so the common case is one less decision; if this
  // carrier does not offer it, the operator must choose (the buy is blocked until
  // they do) rather than us guessing a location the driver would not find. Switching
  // to a carrier with no locations clears the choice, so a code picked for Purolator
  // can never ride along on another carrier's shipment.
  useEffect(() => {
    if (!pickupDate || !pickupLocationsKnown) return;
    if (pickupLocations.some((l) => l.code === pickupLocation)) return;
    const preferred = pickupLocations.find((l) => l.code === DEFAULT_PICKUP_LOCATION_CODE);
    setPickupLocation(preferred ? preferred.code : '');
  }, [pickupDate, pickupLocationsKnown, pickupLocations, pickupLocation]);

  // Pickup requests this draft can ATTACH to (one driver stop for several labels).
  // Only offered on the rates step with a rate chosen and NO new pickup window
  // scheduled — the two are exclusive. Hidden entirely while empty (today every
  // pickup on the account is a single-shipment request, so the common case adds
  // no noise). A failed lookup just keeps it hidden; it never blocks the flow.
  const eligiblePickupsQuery = useEligiblePickups(
    ratesResult?.draftId ?? null,
    selectedRate?.serviceAgreementId ?? null,
    step === 'rates' && !pickupDate,
  );
  const eligiblePickups = useMemo(
    () => eligiblePickupsQuery.data?.pickups ?? [],
    [eligiblePickupsQuery.data],
  );

  // Eligibility is service-scoped: a pickup attached for one carrier must never
  // ride along when the operator switches tier (a Purolator pickup cannot collect
  // a PC-courier box), so the choice resets with the selection.
  useEffect(() => {
    setAttachPickupNumber('');
  }, [selectedRate?.serviceAgreementId]);

  // Selecting a pickup date advances a past From-Time to a valid one. Galaxie
  // rejects a From-Time in the past, and the static 09:00 default is already past
  // for most of the workday when the operator schedules a same-day pickup.
  const handlePickupDateChange = (value: string) => {
    setPickupDate(value);
    setBuyError(null);
    // Scheduling a NEW window and attaching to an existing pickup are exclusive —
    // drop any attachment chosen earlier so it cannot ride along on the windowed buy.
    if (value) setAttachPickupNumber('');
    if (!value || value !== montrealToday()) return;
    const fromAt = montrealInstant(value, pickupFrom).getTime();
    if (Number.isNaN(fromAt) || fromAt <= Date.now()) {
      const nextFrom = nextRoundedMontrealTime();
      setPickupFrom(nextFrom);
      if (timeToMinutes(pickupTo) <= timeToMinutes(nextFrom)) {
        setPickupTo(minutesToTime(timeToMinutes(nextFrom) + 120));
      }
    }
  };

  // Validate the optional pickup window against Galaxie's rules (future From-Time,
  // ordered window, within 7 days). Returns an error message or null. A blank date
  // means "no pickup" and is always valid.
  const pickupValidationError = (): string | null => {
    if (!pickupDate) return null;
    if (pickupLocations.length > 0 && !pickupLocation) {
      return t('labelFlow.pickupLocationRequired', 'Choose where the driver picks the package up.');
    }
    if (!pickupFrom || !pickupTo) {
      return t('labelFlow.pickupIncomplete', 'Enter a pickup start and end time, or clear the date.');
    }
    const fromAt = montrealInstant(pickupDate, pickupFrom).getTime();
    const toAt = montrealInstant(pickupDate, pickupTo).getTime();
    if (Number.isNaN(fromAt) || Number.isNaN(toAt)) return null;
    if (fromAt <= Date.now()) {
      return t('labelFlow.pickupPast', 'Pickup start time must be in the future. Choose a later time or a future date.');
    }
    if (toAt <= fromAt) {
      return t('labelFlow.pickupOrder', 'Pickup end time must be after the start time.');
    }
    if (fromAt > montrealInstant(montrealDatePlus(7), '23:59').getTime()) {
      return t('labelFlow.pickupTooFar', 'Pickup must be scheduled within the next 7 days.');
    }
    return null;
  };

  const handleBuy = () => {
    if (!selectedRate || ratesResult == null) return;
    const pickupError = pickupValidationError();
    if (pickupError) {
      setBuyError(pickupError);
      return;
    }
    setBuyError(null);
    const input: ConfirmShipmentInput = {
      draftId: ratesResult.draftId,
      serviceAgreementId: selectedRate.serviceAgreementId,
      carrierCode: selectedRate.carrierCode,
      serviceName: selectedRate.serviceName ?? undefined,
      quoteId: effectiveQuoteId,
      mongoQuoteId: effectiveMongoQuoteId,
      // Three exclusive modes: ATTACH to an existing pickup (its request number
      // only — the window/location live on that pickup), a NEW pickup window, or
      // no pickup at all (drop-off / carrier default).
      pickup: !pickupDate && attachPickupNumber
        ? { requestNumber: attachPickupNumber }
        : pickupDate
          ? {
            date: pickupDate,
            from: pickupFrom,
            to: pickupTo,
            ...(pickupLocation ? { instructionCode: pickupLocation } : {}),
          }
          : null,
    };
    confirmShipment.mutate(input, {
      onSuccess: (createdShipment) => {
        window.open(getShipmentLabelUrl(createdShipment.number), '_blank');
        setCreated(createdShipment);
        setStep('done');
      },
    });
  };

  const handlePrintAgain = () => {
    if (!created) return;
    window.open(getShipmentLabelUrl(created.number), '_blank');
  };

  const handleCancel = () => {
    if (!created) return;
    cancelShipment.mutate({ number: created.number }, { onSuccess: () => setCancelled(true) });
  };

  // Void a previously-created label from the existing-labels list. The list
  // refetches on success (its query key is nested under pcKeys.shipments()).
  const handleVoidLabel = (number: string) => {
    cancelShipment.mutate({ number });
  };

  const handleClose = () => onOpenChange(false);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            {t('labelFlow.title')}
          </DialogTitle>
        </DialogHeader>

        {!showQuotePicker && step !== 'existing' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className={cn(step === 'details' && 'font-semibold text-foreground')}>{t('labelFlow.stepDetails')}</span>
            <span>&rarr;</span>
            <span className={cn(step === 'rates' && 'font-semibold text-foreground')}>{t('labelFlow.stepRates')}</span>
            <span>&rarr;</span>
            <span className={cn(step === 'done' && 'font-semibold text-foreground')}>{t('labelFlow.stepDone')}</span>
          </div>
        )}

        {showQuotePicker ? (
          <div className="space-y-2">
            <Label>{t('labelFlow.selectQuote')}</Label>
            <Popover open={quotePopoverOpen} onOpenChange={setQuotePopoverOpen} modal={false}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" aria-expanded={quotePopoverOpen} className="w-full justify-between">
                  <span className="text-muted-foreground">{t('labelFlow.selectQuote')}</span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[460px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput placeholder={t('labelFlow.searchQuotePlaceholder')} value={quoteSearch} onValueChange={setQuoteSearch} />
                  <CommandList>
                    {quotesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">{t('labelFlow.loadingQuotes')}</span>
                      </div>
                    ) : quotes.length === 0 ? (
                      <CommandEmpty>{t('labelFlow.noQuotesFound')}</CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {quotes.map((quote) => (
                          <CommandItem key={quote.id} value={quote.id} onSelect={() => handleQuoteSelect(quote)} className="flex items-center py-2">
                            <Check className="mr-2 h-4 w-4 opacity-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">
                                {quote.quote_number}
                                {quote.account_name && ` - ${quote.account_name}`}
                              </div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {labelContext.isPending && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('labelFlow.loadingQuotes')}
              </div>
            )}
          </div>
        ) : (
          <>
            {step === 'existing' && (
              <div className="space-y-4">
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t('labelFlow.existingWarning', 'This quote already has a shipping label. Print or void it below, or create another.')}</span>
                </div>
                <div className="space-y-2">
                  {(quoteShipments ?? []).map((s) => {
                    const voided = s.status === 'Cancelled';
                    return (
                      <div key={s.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 font-medium">
                            <span className="truncate">{s.serviceName || s.carrierCode || t('labelFlow.label', 'Label')}</span>
                            <span className="text-muted-foreground">· {s.shipmentNumber}</span>
                            {voided && <Badge variant="secondary">{t('labelFlow.cancelledBadge', 'voided')}</Badge>}
                          </div>
                          <div className="text-sm text-muted-foreground">{formatDate(s.createdAt)}</div>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(getShipmentLabelUrl(s.shipmentNumber), '_blank')}
                          >
                            <Printer className="mr-1 h-4 w-4" />
                            {t('labelFlow.print', 'Print')}
                          </Button>
                          {!voided && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={cancelShipment.isPending}
                              onClick={() => handleVoidLabel(s.shipmentNumber)}
                            >
                              <Trash2 className="mr-1 h-4 w-4" />
                              {t('labelFlow.void', 'Void')}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button type="button" onClick={() => setStep('details')}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('labelFlow.createAnother', 'Create another label')}
                </Button>
              </div>
            )}

            {step === 'details' && (
              <div className="space-y-4">
                {labelContext.isPending && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('labelFlow.loadingDetails')}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-base">{t('labelFlow.destinationAddress')}</Label>
                  {accountSites.length > 0 && (
                    <Select value={selectedSiteId} onValueChange={handleSiteSelect}>
                      <SelectTrigger aria-label={t('labelFlow.destinationSite', 'Destination address (from client sites)')}>
                        <SelectValue placeholder={t('labelFlow.destinationSite', 'Destination address (from client sites)')} />
                      </SelectTrigger>
                      <SelectContent>
                        {accountSites.map((s) => (
                          <SelectItem key={siteOptionValue(s)} value={siteOptionValue(s)}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Input aria-label={t('labelFlow.addressName')} placeholder={t('labelFlow.addressName')} value={destination.name} onChange={(e) => setDestination({ ...destination, name: e.target.value })} />
                  <Input aria-label={t('labelFlow.addressLine1')} placeholder={t('labelFlow.addressLine1')} value={destination.addressLine1} onChange={(e) => setDestination({ ...destination, addressLine1: e.target.value })} />
                  <Input aria-label={t('labelFlow.addressLine2')} placeholder={t('labelFlow.addressLine2')} value={destination.addressLine2} onChange={(e) => setDestination({ ...destination, addressLine2: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input aria-label={t('labelFlow.city')} placeholder={t('labelFlow.city')} value={destination.city} onChange={(e) => setDestination({ ...destination, city: e.target.value })} />
                    <Input aria-label={t('labelFlow.stateProvince')} placeholder={t('labelFlow.stateProvince')} value={destination.stateProvince} onChange={(e) => setDestination({ ...destination, stateProvince: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input aria-label={t('labelFlow.postalCode')} placeholder={t('labelFlow.postalCode')} value={destination.postalCode} onChange={(e) => setDestination({ ...destination, postalCode: e.target.value })} />
                    <Input aria-label={t('labelFlow.country')} placeholder={t('labelFlow.country')} value={destination.country} onChange={(e) => setDestination({ ...destination, country: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-base">{t('labelFlow.recipientContact')}</Label>
                  <Input aria-label={t('labelFlow.contactName')} placeholder={t('labelFlow.contactName')} value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input aria-label={t('labelFlow.contactEmail')} placeholder={t('labelFlow.contactEmail')} value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} />
                    <Input aria-label={t('labelFlow.contactPhone')} placeholder={t('labelFlow.contactPhone')} value={recipient.phone} onChange={(e) => setRecipient({ ...recipient, phone: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="pc-references">{t('labelFlow.references')}</Label>
                  <Input id="pc-references" placeholder={t('labelFlow.referencesHint')} value={referencesText} onChange={(e) => setReferencesText(e.target.value)} />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="pc-declared-value">{t('labelFlow.declaredValue')}</Label>
                  <Input id="pc-declared-value" type="number" inputMode="decimal" value={declaredValueText} onChange={(e) => setDeclaredValueText(e.target.value)} />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">{t('labelFlow.packages')}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addPackage}>
                      <Plus className="h-4 w-4 mr-1" />
                      {t('labelFlow.addPackage')}
                    </Button>
                  </div>
                  {weightIsEstimate && (
                    <p className="text-xs text-muted-foreground italic">{t('labelFlow.weightEstimate')}</p>
                  )}
                  <div className="space-y-2">
                    {packages.map((pkg, index) => (
                      <PackageRow
                        key={pkg.key}
                        pkg={pkg}
                        index={index}
                        presets={presets ?? []}
                        onChange={updatePackage}
                        onRemove={() => removePackage(pkg.key)}
                        canRemove={packages.length > 1}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-base">{t('labelFlow.pickupTitle', 'Pickup (optional)')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('labelFlow.pickupHint', 'Schedule a carrier pickup. Leave the date blank for no scheduled pickup.')}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor="pc-pickup-date" className="text-xs">{t('labelFlow.pickupDate', 'Date')}</Label>
                      <Input
                        id="pc-pickup-date"
                        type="date"
                        min={montrealToday()}
                        max={montrealDatePlus(7)}
                        value={pickupDate}
                        onChange={(e) => handlePickupDateChange(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pc-pickup-from" className="text-xs">{t('labelFlow.pickupFrom', 'From')}</Label>
                      <Input id="pc-pickup-from" type="time" value={pickupFrom} onChange={(e) => { setPickupFrom(e.target.value); setBuyError(null); }} disabled={!pickupDate} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pc-pickup-to" className="text-xs">{t('labelFlow.pickupTo', 'To')}</Label>
                      <Input id="pc-pickup-to" type="time" value={pickupTo} onChange={(e) => { setPickupTo(e.target.value); setBuyError(null); }} disabled={!pickupDate} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pc-pickup-notes" className="text-xs">
                      {t('labelFlow.pickupNotes', 'Note for the driver (optional)')}
                    </Label>
                    <Input
                      id="pc-pickup-notes"
                      value={pickupNotes}
                      onChange={(e) => setPickupNotes(e.target.value)}
                      disabled={!pickupDate}
                      maxLength={MAX_PICKUP_NOTES}
                      placeholder={t('labelFlow.pickupNotesPlaceholder', 'e.g. Suite 240, reception')}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('labelFlow.pickupNotesHint', 'Where to go once inside the building. 50 characters max.')}
                    </p>
                  </div>
                </div>

                {effectiveQuoteId && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={alsoRma} onCheckedChange={(v) => setAlsoRma(v === true)} />
                    {t('operations:rma.fromQuote.alsoGenerateReturn', 'Also generate an RMA return label')}
                  </label>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    {t('labelFlow.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleGetRates}
                    disabled={calculateRates.isPending || labelContext.isPending}
                  >
                    {(calculateRates.isPending || labelContext.isPending) && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    {t('labelFlow.getRates')}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {step === 'rates' && (
              <div className="space-y-4">
                <Label className="text-base">{t('labelFlow.chooseCarrier')}</Label>
                <RateList rates={rateOptions} selectedKey={selectedRateKey} onSelect={setSelectedRateKey} />

                {pickupDate && pickupLocations.length > 0 && (
                  <div className="space-y-1">
                    <Label htmlFor="pc-pickup-location" className="text-sm">
                      {t('labelFlow.pickupLocation', 'Pickup location')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('labelFlow.pickupLocationHint', 'Where the driver picks the package up. This carrier requires it.')}
                    </p>
                    <Select
                      value={pickupLocation}
                      onValueChange={(value) => { setPickupLocation(value); setBuyError(null); }}
                    >
                      <SelectTrigger id="pc-pickup-location">
                        <SelectValue placeholder={t('labelFlow.pickupLocationPlaceholder', 'Choose a pickup location')} />
                      </SelectTrigger>
                      <SelectContent>
                        {pickupLocations.map((location) => (
                          <SelectItem key={location.code} value={location.code}>{location.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!pickupDate && eligiblePickups.length > 0 && (
                  <div className="space-y-1">
                    <Label htmlFor="pc-attach-pickup" className="text-sm">
                      {t('labelFlow.attachPickupTitle', 'Attach to an existing pickup')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('labelFlow.attachPickupHint', 'Add this box to a pickup already scheduled for this carrier — one driver collects everything.')}
                    </p>
                    <Select
                      value={attachPickupNumber || ATTACH_NONE}
                      onValueChange={(value) => { setAttachPickupNumber(value === ATTACH_NONE ? '' : value); setBuyError(null); }}
                    >
                      <SelectTrigger id="pc-attach-pickup">
                        <SelectValue placeholder={t('labelFlow.attachPickupPlaceholder', 'Choose a pickup to join')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ATTACH_NONE}>
                          {t('labelFlow.attachPickupNone', 'No — separate pickup / drop-off')}
                        </SelectItem>
                        {eligiblePickups.map((p) => (
                          <SelectItem key={p.number} value={p.number}>
                            {formatEligiblePickupOption(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {buyError && (
                  <p className="text-sm text-destructive" role="alert">{buyError}</p>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setBuyError(null); setStep('details'); }}>
                    {t('labelFlow.back')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleBuy}
                    disabled={!selectedRate || confirmShipment.isPending || pickupLocationsQuery.isLoading}
                  >
                    {confirmShipment.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    {confirmShipment.isPending ? t('labelFlow.buying') : t('labelFlow.buyLabel')}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {step === 'done' && created && (
              <div className="space-y-4">
                <p className="text-sm font-medium">
                  {cancelled ? t('labelFlow.cancelled') : t('labelFlow.created')}
                </p>
                <div className="rounded-md border p-3 space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('labelFlow.shipmentNumber')}: </span>
                    <span className="font-medium">{created.number}</span>
                    {cancelled && (
                      <span className="ml-2 text-destructive">
                        ({t('labelFlow.cancelledBadge', 'voided')})
                      </span>
                    )}
                  </div>
                </div>

                {alsoRma && effectiveQuoteId && (
                  <div className="rounded-md border border-dashed p-3">
                    <p className="text-sm font-medium mb-2">
                      {t('operations:rma.fromQuote.returnStepTitle', 'Return label (RMA)')}
                    </p>
                    <Button type="button" variant="outline" size="sm" onClick={() => setRmaOpen(true)}>
                      <Undo2 className="h-4 w-4 mr-1" />
                      {t('operations:rma.fromQuote.button', 'Create RMA')}
                    </Button>
                  </div>
                )}

                <DialogFooter>
                  {!cancelled && (
                    <>
                      <Button type="button" variant="outline" onClick={handlePrintAgain}>
                        <Printer className="h-4 w-4 mr-1" />
                        {t('labelFlow.printAgain')}
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={handleCancel}
                        disabled={cancelShipment.isPending}
                      >
                        {cancelShipment.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                        {t('labelFlow.cancelShipment', 'Cancel shipment')}
                      </Button>
                    </>
                  )}
                  <Button type="button" onClick={handleClose}>
                    {t('labelFlow.close')}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
    {rmaOpen && effectiveQuoteId && (
      <CreateRmaFromQuoteModal
        open={rmaOpen}
        onOpenChange={setRmaOpen}
        quoteId={effectiveQuoteId}
        source="shipping"
      />
    )}
    </>
  );
}
